
/* ==========================================================================
   TRANQUIL'O — Choréographie de scroll
   Lenis (scroll fluide) + GSAP ScrollTrigger.

   L'acte central accepte deux moteurs de rendu :
   · séquence d'images dessinée sur un canvas (window.TRANQUILO_FRAMES) —
     rendu parfaitement fluide, aucun décodage à la volée ;
   · à défaut, pilotage du currentTime d'un <video>, avec une seule recherche
     en vol à la fois (sans quoi les seeks s'empilent et l'image saccade).
   ========================================================================== */
(function () {
  "use strict";

function boot() {

  var hasGSAP = typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none)").matches;

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  var canvas = $("#product-canvas");
  var video = $("#product-video");
  var frameUrls = window.TRANQUILO_FRAMES || [];
  var useFrames = !!(canvas && frameUrls.length);

  /* Le scrub d'un <video> n'est fiable ni sur tactile (iOS bufferise mal les
     recherches) ni en mouvement réduit : on bascule en lecture bouclée. */
  var canScrub = hasGSAP && !isTouch && !reduced;

  /* ------------------------------------------------------------------
     1. Moteur de rendu par séquence d'images
     ------------------------------------------------------------------ */
  /* La source porte un filigrane dans sa bande basse : on la rogne à la
     source plutôt que d'agrandir l'image, ce qui couperait les extrémités
     de la vue éclatée. */
  var SRC_CROP_BOTTOM = 0.085;

  var ctx = useFrames ? canvas.getContext("2d", { alpha: false }) : null;
  var frames = [];
  var framesLoaded = 0;
  var currentIndex = -1;
  var bgColor = "#d9dcde";

  function sampleBg(img) {
    try {
      var c = document.createElement("canvas");
      c.width = 1; c.height = 1;
      var cx = c.getContext("2d");
      cx.drawImage(img, 3, 3, 6, 6, 0, 0, 1, 1);
      var d = cx.getImageData(0, 0, 1, 1).data;
      bgColor = "rgb(" + d[0] + "," + d[1] + "," + d[2] + ")";
    } catch (e) { /* canvas teinté : on garde la couleur par défaut */ }
  }

  function paint(img, alpha) {
    var cw = canvas.width, ch = canvas.height;
    var iw = img.naturalWidth;
    var ih = Math.round(img.naturalHeight * (1 - SRC_CROP_BOTTOM));

    /* Ajustement « contain » : la vue éclatée occupe toute la largeur de
       l'image, elle ne doit jamais être rognée sur les côtés. Le fond studio
       étant uni, la couleur échantillonnée comble le reste sans raccord. */
    var scale = Math.min(cw / iw, ch / ih);
    var dw = iw * scale, dh = ih * scale;

    ctx.globalAlpha = alpha;
    ctx.drawImage(img, 0, 0, iw, ih, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    ctx.globalAlpha = 1;
  }

  /* La source est à 24 i/s : à l'échelle du défilement, passer d'une image à
     la suivante se verrait. On dessine donc la position exacte entre deux
     images en fondu, ce qui rend le mouvement continu. */
  function drawAt(exact, force) {
    if (!ctx || !frames.length) return;
    if (!force && Math.abs(exact - currentIndex) < 0.008) return;
    currentIndex = exact;

    var last = frameUrls.length - 1;
    var i = Math.max(0, Math.min(Math.floor(exact), last));
    var next = Math.min(i + 1, last);
    var t = exact - i;

    var a = frames[i];
    var b = frames[next];
    var aReady = a && a.complete && a.naturalWidth;
    var bReady = b && b.complete && b.naturalWidth;
    if (!aReady && !bReady) return;

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (t >= 0.985 && bReady) {
      paint(b, 1);
    } else {
      if (aReady) paint(a, 1);
      if (bReady && t > 0.015 && next !== i) paint(b, aReady ? t : 1);
    }
  }

  function drawFrame(i, force) { drawAt(i, force); }

  function sizeCanvas() {
    if (!canvas) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = canvas.clientWidth || window.innerWidth;
    var h = canvas.clientHeight || window.innerHeight;
    var cw = Math.min(Math.round(w * dpr), 2400);
    var ratio = cw / (w || 1);
    canvas.width = cw;
    canvas.height = Math.round(h * ratio);
    drawFrame(currentIndex < 0 ? 0 : currentIndex, true);
  }

  function loadFrame(i, onDone) {
    var img = new Image();
    img.decoding = "async";

    /* Une image « chargée » n'est pas encore décodée : le navigateur décode
       au premier dessin, ce qui produit une micro-coupure par image la
       première fois qu'on la traverse. On décode donc tout d'avance, pendant
       l'écran de chargement, pour que le défilement ne décode plus rien. */
    var finish = function () {
      framesLoaded++;
      if (i % 20 === 0) sampleBg(img);
      if (i === 0) { sizeCanvas(); drawFrame(0, true); }
      onDone && onDone();
    };

    img.onload = function () {
      if (typeof img.decode === "function") {
        img.decode().then(finish, finish);
      } else {
        finish();
      }
    };
    img.onerror = function () { framesLoaded++; onDone && onDone(); };
    frames[i] = img;
    img.src = frameUrls[i];
  }

  /* ------------------------------------------------------------------
     2. Écran de chargement
     ------------------------------------------------------------------ */
  var loader = $("#loader");
  var loaderBar = $("#loader-bar");
  var heroImg = $("#hero-media img");

  var weights = { image: 30, media: 45, page: 25 };
  var done = { image: false, media: false, page: false };
  var mediaRatio = 0;

  function loaderProgress() {
    var pct = 0;
    for (var k in done) { if (done[k]) pct += weights[k]; }
    if (!done.media) pct += weights.media * mediaRatio;
    if (loaderBar) loaderBar.style.width = Math.round(pct) + "%";
    if (pct >= 100) finishLoading();
  }

  var loadingFinished = false;
  function finishLoading() {
    if (loadingFinished) return;
    loadingFinished = true;
    if (loaderBar) loaderBar.style.width = "100%";
    setTimeout(function () {
      if (loader) loader.classList.add("is-done");
      document.body.classList.remove("is-loading");
      startHero();
      if (hasGSAP) ScrollTrigger.refresh();
    }, 320);
  }

  if (heroImg) {
    if (heroImg.complete) { done.image = true; }
    else {
      heroImg.addEventListener("load", function () { done.image = true; loaderProgress(); });
      heroImg.addEventListener("error", function () { done.image = true; loaderProgress(); });
    }
  } else { done.image = true; }

  if (useFrames) {
    /* Deux temps : les 12 premières images pour un premier rendu rapide,
       le reste en arrière-plan. */
    var FIRST = Math.min(12, frameUrls.length);
    var firstDone = 0;

    var onFirst = function () {
      firstDone++;
      mediaRatio = (firstDone / FIRST) * 0.4;
      loaderProgress();
      if (firstDone === FIRST) {
        var rest = frameUrls.length - FIRST;
        if (!rest) { done.media = true; loaderProgress(); return; }
        var restDone = 0;
        var onRest = function () {
          restDone++;
          mediaRatio = 0.4 + (restDone / rest) * 0.6;
          if (restDone === rest) { done.media = true; }
          loaderProgress();
        };
        for (var j = FIRST; j < frameUrls.length; j++) { loadFrame(j, onRest); }
      }
    };
    for (var i = 0; i < FIRST; i++) { loadFrame(i, onFirst); }
  } else if (video) {
    if (video.readyState >= 2) { done.media = true; }
    else {
      video.addEventListener("loadeddata", function () { done.media = true; loaderProgress(); });
      video.addEventListener("error", function () { done.media = true; loaderProgress(); });
    }
  } else { done.media = true; }

  window.addEventListener("load", function () { done.page = true; loaderProgress(); });

  /* Filet de sécurité : on n'enferme jamais le visiteur derrière le loader. */
  setTimeout(finishLoading, 14000);
  loaderProgress();

  /* ------------------------------------------------------------------
     3. Repli sans GSAP — tout reste lisible
     ------------------------------------------------------------------ */
  if (!hasGSAP) {
    document.documentElement.classList.remove("js");
    $$("[data-reveal]").forEach(function (el) { el.style.opacity = 1; });
    $$(".hero-line > span").forEach(function (el) { el.style.transform = "none"; });

    var actEl = $(".act-video"), stageEl = $(".video-stage"), copyEl = $(".video-copy");
    if (actEl) actEl.style.height = "auto";
    if (stageEl) { stageEl.style.position = "relative"; stageEl.style.height = "70vh"; }
    if (copyEl) {
      copyEl.style.position = "static";
      copyEl.style.display = "grid";
      copyEl.style.gap = "48px";
      copyEl.style.padding = "64px 6vw";
    }
    $$(".copy-block").forEach(function (el) {
      el.style.opacity = 1;
      el.style.position = "static";
      el.style.transform = "none";
      el.style.width = "auto";
      el.style.textAlign = "left";
    });
    var clipEl = $("#video-clip");
    if (clipEl) clipEl.style.clipPath = "none";
    var statsEl = $("#video-stats");
    if (statsEl) statsEl.style.display = "none";
    if (video) { video.loop = true; video.play().catch(function () {}); }
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ------------------------------------------------------------------
     4. Scroll fluide Lenis
     ------------------------------------------------------------------ */
  var lenis = null;
  if (typeof window.Lenis !== "undefined" && !reduced) {
    lenis = new Lenis({
      duration: 1.3,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      touchMultiplier: 1.6
    });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  $$("[data-goto]").forEach(function (el) {
    el.addEventListener("click", function () {
      window.location.href = el.getAttribute("data-goto");
    });
  });

  $$('a[href^="#"], [data-scroll-to]').forEach(function (link) {
    link.addEventListener("click", function (e) {
      var sel = link.getAttribute("data-scroll-to") || link.getAttribute("href");
      if (!sel || sel === "#") return;
      var target = document.querySelector(sel);
      if (!target) return;
      e.preventDefault();
      if (lenis) lenis.scrollTo(target, { offset: -10, duration: 1.4 });
      else target.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
    });
  });

  /* ------------------------------------------------------------------
     5. Header
     ------------------------------------------------------------------ */
  var header = $("#site-header");
  if (header) {
    ScrollTrigger.create({
      trigger: ".hero",
      start: "bottom top+=90",
      onEnter: function () { header.classList.add("is-stuck"); },
      onLeaveBack: function () { header.classList.remove("is-stuck"); }
    });
  }

  /* ------------------------------------------------------------------
     6. Hero — révélation ligne par ligne + parallaxe
     ------------------------------------------------------------------ */
  function startHero() {
    if (!hasGSAP || !$(".hero")) return;
    var lines = $$(".hero-line > span");
    var tl = gsap.timeline({ defaults: { ease: "power4.out" } });
    tl.to(lines, { y: "0%", duration: 1.25, stagger: 0.11 })
      .from(".hero .label", { opacity: 0, y: 16, duration: 0.8 }, 0.15)
      .from(".hero-lede", { opacity: 0, y: 22, duration: 0.9 }, 0.5)
      .from(".hero-actions .btn", { opacity: 0, y: 20, duration: 0.8, stagger: 0.09 }, 0.65)
      .from(".hero-meta", { opacity: 0, duration: 1 }, 0.8);
  }

  if (!reduced && $(".hero")) {
    gsap.to("#hero-media", {
      yPercent: 14,
      ease: "none",
      scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true }
    });
    gsap.to(".hero-inner", {
      yPercent: -8,
      opacity: 0.15,
      ease: "none",
      scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true }
    });
  }

  /* Jauge de lecture */
  var progress = $("#scroll-progress");
  if (progress) {
    gsap.to(progress, {
      scaleX: 1,
      ease: "none",
      scrollTrigger: { start: 0, end: "max", scrub: 0.3 }
    });
  }

  /* ------------------------------------------------------------------
     7. Marquee horizontal
     ------------------------------------------------------------------ */
  var marquee = $("#marquee");
  if (marquee && !reduced) {
    /* Un effet lié au scroll (molette ou tactile) ne laisse jamais le temps
       de lire la phrase, sur ordinateur comme sur mobile : trop court, ou
       trop dépendant de la vitesse de défilement de la personne. Défilement
       continu et autonome à la place — la bande contient 4 copies identiques
       de la phrase, donc un déplacement de -25% (une copie) en boucle
       infinie donne un défilement parfaitement fluide. */
    gsap.fromTo(marquee, { xPercent: 0 }, {
      xPercent: -25,
      ease: "none",
      duration: 9,
      repeat: -1
    });
  }

  /* ------------------------------------------------------------------
     8. ACTE PRODUIT — révélation, séquence, blocs latéraux, chiffres
     ------------------------------------------------------------------ */
  var act = $(".act-video");
  var clip = $("#video-clip");
  var veil = $("#video-veil");
  var stats = $("#video-stats");

  /* L'animation produit se termine vers 77 % du défilement, laissant le
     dernier quart au voile sombre et aux chiffres. */
  var MEDIA_SPEED = 1.30;

  /* Repli <video> : une seule recherche en vol à la fois. */
  var targetTime = 0;
  var appliedTime = -1;
  var scrubActive = false;

  if (!useFrames && video && canScrub) {
    video.pause();
    var pump = function () {
      if (scrubActive && video.readyState >= 2 && !video.seeking &&
          Math.abs(targetTime - appliedTime) > 0.02) {
        appliedTime = targetTime;
        try { video.currentTime = targetTime; } catch (err) { /* recherche en cours */ }
      }
      requestAnimationFrame(pump);
    };
    requestAnimationFrame(pump);
  } else if (!useFrames && video) {
    video.loop = true;
    video.muted = true;
    var tryPlay = function () { video.play().catch(function () {}); };
    tryPlay();
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden) tryPlay();
    });
  }

  /* Le défilement à la molette arrive par crans. Plutôt que de coller à ces
     crans, on fait glisser la position de lecture vers sa cible à chaque
     rafraîchissement : le mouvement du produit devient continu même quand
     l'entrée, elle, est saccadée. */
  var targetExact = 0;
  var renderExact = 0;

  if (useFrames) {
    sizeCanvas();
    window.addEventListener("resize", sizeCanvas);

    var frameLoop = function () {
      var d = targetExact - renderExact;
      if (Math.abs(d) > 0.003) {
        renderExact += d * (Math.abs(d) > 6 ? 0.34 : 0.15);
        drawAt(renderExact);
      }
      requestAnimationFrame(frameLoop);
    };
    requestAnimationFrame(frameLoop);
  }

  /* Révélation du produit PENDANT que la section monte à l'écran, et non une
     fois qu'elle est fixée : sans cela on traverse un plein écran vide entre
     le bandeau bleu et l'apparition du détecteur. */
  if (clip && !reduced && act) {
    ScrollTrigger.create({
      trigger: act,
      start: "top 88%",
      end: "top top",
      scrub: 0.5,
      onUpdate: function (self) {
        clip.style.clipPath = "circle(" + (self.progress * 100).toFixed(2) + "% at 50% 52%)";
      }
    });
  } else if (clip && reduced) {
    clip.style.clipPath = "none";
  }

  var FROM = {
    "slide-left":  { x: reduced ? 0 : -90, opacity: 0 },
    "slide-right": { x: reduced ? 0 :  90, opacity: 0 },
    "clip-reveal": reduced
      ? { opacity: 0 }
      : { clipPath: "inset(100% 0 0 0)", y: 26, opacity: 0 },
    "scale-up":    { scale: reduced ? 1 : 0.9, opacity: 0 },
    "fade-up":     { y: reduced ? 0 : 46, opacity: 0 }
  };

  function buildCopyTimeline(block, type) {
    var kids = $$(".label, .h-3, .lede, .copy-note", block);
    /* L'entrée occupe le premier tiers du créneau, la sortie ne commence
       qu'à 92 % : le texte reste lisible sur l'essentiel du défilement. */
    var vars = { duration: 0.20, stagger: 0.045, opacity: 0,
                 ease: type === "clip-reveal" ? "power4.out" : "power3.out" };
    Object.keys(FROM[type]).forEach(function (k) { vars[k] = FROM[type][k]; });

    var tl = gsap.timeline({ paused: true });
    tl.set(block, { opacity: 1 }, 0);
    tl.from(kids, vars, 0);
    tl.to(block, { opacity: 0, y: reduced ? 0 : -26, duration: 0.08, ease: "power2.in" }, 0.92);
    return tl;
  }

  /* Les créneaux de défilement sont répartis entre les blocs présents :
     la section Shopify peut en compter 2, 3 ou 4. */
  var COPY_START = 0.10, COPY_END = 0.76;
  var DEFAULT_TYPES = ["slide-left", "slide-right", "clip-reveal", "scale-up"];
  var blockEls = $$(".copy-block");
  var slot = (COPY_END - COPY_START) / Math.max(blockEls.length, 1);

  var blocks = blockEls.map(function (el, i) {
    var type = el.getAttribute("data-animation");
    if (!FROM[type]) type = DEFAULT_TYPES[i % DEFAULT_TYPES.length];
    return {
      el: el,
      enter: COPY_START + i * slot,
      leave: COPY_START + (i + 1) * slot - 0.02,
      tl: buildCopyTimeline(el, type)
    };
  });

  var countersFired = false;
  function runCounters() {
    if (countersFired) return;
    countersFired = true;
    $$(".stat-number").forEach(function (el) {
      var target = parseFloat(el.dataset.value);
      var dec = parseInt(el.dataset.decimals || "0", 10);
      var obj = { v: 0 };
      gsap.to(obj, {
        v: target,
        duration: 1.8,
        ease: "power2.out",
        onUpdate: function () { el.textContent = obj.v.toFixed(dec).replace(".", ","); }
      });
    });
  }

  if (act) {
    ScrollTrigger.create({
      trigger: act,
      start: "top top",
      end: "bottom bottom",
      scrub: true,
      onUpdate: function (self) {
        var p = self.progress;

        /* Avancement du média */
        var mp = clamp(p * MEDIA_SPEED, 0, 1);
        if (useFrames) {
          targetExact = mp * (frameUrls.length - 1);
        } else if (video && canScrub && video.duration) {
          scrubActive = true;
          targetTime = mp * (video.duration - 0.05);
        }

        /* Blocs de texte */
        blocks.forEach(function (b) {
          var n = (p - b.enter) / (b.leave - b.enter);
          if (n <= 0) { b.tl.progress(0); b.el.style.opacity = 0; }
          else if (n >= 1) { b.tl.progress(1); }
          else { b.tl.progress(n); }
        });

        /* Voile sombre + chiffres */
        var veilOp = 0;
        if (p >= 0.77 && p < 0.82) veilOp = (p - 0.77) / 0.05;
        else if (p >= 0.82 && p < 0.955) veilOp = 1;
        else if (p >= 0.955 && p <= 1) veilOp = clamp(1 - (p - 0.955) / 0.045, 0, 1);
        if (veil) veil.style.opacity = (veilOp * 0.92).toFixed(3);

        var statOp = clamp((p - 0.80) / 0.04, 0, 1) * clamp(1 - (p - 0.955) / 0.04, 0, 1);
        if (stats) {
          stats.style.opacity = statOp.toFixed(3);
          stats.style.transform = "translateY(" + ((1 - statOp) * 26).toFixed(1) + "px)";
        }
        if (p >= 0.80) runCounters();
      }
    });
  }

  /* ------------------------------------------------------------------
     9. Révélations de section — une entrée différente par section
     ------------------------------------------------------------------ */
  var REST = { x: 0, y: 0, scale: 1, rotation: 0, clipPath: "inset(0% 0 0 0)" };

  /* Révélation par défaut, appliquée aux éléments qu'aucun appel n'a couverts. */
  function reveal2(el) {
    el.dataset.revealed = "1";
    gsap.fromTo(el, { opacity: 0, y: 36 }, {
      opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 88%", once: true }
    });
  }

  function reveal(selector, opts) {
    var els = $$(selector);
    if (!els.length) return;

    var from = { opacity: 0 };
    var to = {
      opacity: 1,
      duration: opts.duration || 0.95,
      stagger: opts.stagger || 0.1,
      ease: opts.ease || "power3.out",
      scrollTrigger: {
        trigger: opts.trigger || els[0],
        start: opts.start || "top 82%",
        once: true          /* une fois révélé, jamais masqué à nouveau */
      }
    };

    Object.keys(opts.from || {}).forEach(function (key) {
      from[key] = opts.from[key];
      to[key] = REST[key];
    });

    els.forEach(function (el) { el.dataset.revealed = "1"; });
    gsap.fromTo(els, from, to);
  }

  if (reduced) {
    $$("[data-reveal]").forEach(function (el) { el.style.opacity = 1; });
  } else {
    /* ATTENTION : ces sélecteurs doivent suivre les identifiants réels des
       sections. Le passage au site multipage a renommé #installation en
       #constat et #fonctionnalites en #rassurance ; les anciens appels ne
       ciblaient plus rien et laissaient ces blocs bloqués à opacity:0.
       Le balayage en fin de fonction sert désormais de filet. */

    /* solution (photo2) : fondu montant */
    reveal(".anywhere-body [data-reveal], .anywhere-demo [data-reveal]", {
      from: { y: 44 }, trigger: ".anywhere", start: "top 68%", stagger: 0.11
    });

    /* constat : glissement de l'intro, bascule des trois observations */
    reveal("#constat .section-head [data-reveal]", {
      from: { x: -60 }, trigger: "#constat", stagger: 0.1
    });
    reveal("#constat .reality-item", {
      from: { y: 56 }, trigger: ".reality", start: "top 84%",
      stagger: 0.13, duration: 1
    });

    /* à propos */
    reveal("#a-propos [data-reveal]", {
      from: { y: 40 }, trigger: "#a-propos", start: "top 80%", stagger: 0.1
    });

    /* rassurance : fondu puis révélation ligne à ligne */
    reveal("#rassurance .section-head [data-reveal]", {
      from: { y: 40 }, trigger: "#rassurance", stagger: 0.1
    });
    reveal("#rassurance .feature", {
      from: { clipPath: "inset(100% 0 0 0)", y: 24 },
      trigger: ".feature-list", start: "top 82%", stagger: 0.08,
      duration: 1.1, ease: "power4.out"
    });

    /* contact de l'accueil */
    reveal("#contact.product [data-reveal]", {
      from: { y: 40 }, trigger: "#contact.product", start: "top 78%", stagger: 0.1
    });

    /* fiche produit : glissement puis agrandissement du bloc d'achat */
    reveal(".product-grid > div [data-reveal]", {
      from: { x: 60 }, trigger: ".product", start: "top 72%", stagger: 0.09
    });
    reveal(".buy", {
      from: { scale: 0.94, y: 30 }, trigger: ".buy", start: "top 86%",
      duration: 1.1, ease: "power2.out"
    });

    /* appel final, quand la page en comporte un */
    reveal(".final [data-reveal]", {
      from: { y: 50 }, trigger: ".final", start: "top 76%", stagger: 0.12
    });

    /* FILET DE SÉCURITÉ — tout [data-reveal] oublié par les appels ci-dessus
       reçoit un fondu montant par défaut. Sans lui, un élément non ciblé
       resterait invisible pour toujours (règle « .js [data-reveal] »). */
    $$("[data-reveal]").forEach(function (el) {
      if (el.dataset.revealed) return;
      reveal2(el);
    });

    /* parallaxe douce sur photo2 */
    if ($("#anywhere-img")) {
      gsap.fromTo("#anywhere-img", { yPercent: -6 }, {
        yPercent: 4,
        ease: "none",
        scrollTrigger: { trigger: ".anywhere", start: "top bottom", end: "bottom top", scrub: true }
      });
    }
  }

  /* ------------------------------------------------------------------
     10. Recalculs
     ------------------------------------------------------------------ */
  window.addEventListener("resize", function () { ScrollTrigger.refresh(); });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
}

  /* ------------------------------------------------------------------
     11. Amorçage — page statique comme thème Shopify
     ------------------------------------------------------------------ */
  var booted = false;
  function start() {
    if (booted) return;
    booted = true;
    boot();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  /* Éditeur de thème Shopify : une section re-rendue perd ses tweens.
     On la rend visible et on recalcule les déclencheurs. */
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelectorAll) {
      Array.prototype.forEach.call(e.target.querySelectorAll("[data-reveal]"), function (el) {
        el.style.opacity = 1;
      });
    }
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
  document.addEventListener("shopify:section:unload", function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();